import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { elasticsearchService } from "../services/elasticsearchService";
import { kanbanApiService } from "../services/kanbanApi";
import { Search, X, LoaderCircle, ArrowRight, KanbanSquare, Users, CornerDownLeft, LineChart } from "lucide-react";
import Fuse from "fuse.js";
import { serverListApiService, type ServerListEntry } from "../services/serverListApi";
import { useOnClickOutside } from "../hooks/useOnClickOutside";
import { PlayerProfile } from "../components/PlayerProfile";
import { KanbanCardTypeBadge } from "../components/kanban/KanbanCardTypeBadge";
import { useDebounce } from "../hooks/useDebounce";
import { usernameCacheService } from "../services/usernameCache";
import type { KanbanCardSummary, KanbanCardType } from "../types/kanban";

interface SearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialQuery?: string | null;
  onResultClick: (uuid: string) => void;
}

interface PlayerSearchResult {
  _id: string;
  _source: {
    uuid: string;
    lastKnownName: string;
    nickname: string;
    lastKnownIp: string;
  };
}

export function SearchModal({ isOpen, onClose, initialQuery, onResultClick }: SearchModalProps) {
  const [query, setQuery] = useState(initialQuery || "");
  const [results, setResults] = useState<PlayerSearchResult[]>([]);
  const [kanbanResults, setKanbanResults] = useState<KanbanCardSummary[]>([]);
  const [servers, setServers] = useState<ServerListEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const modalRef = useRef<HTMLDivElement>(null);
  useOnClickOutside(modalRef, onClose);
  const inputRef = useRef<HTMLInputElement>(null);

  const debouncedQuery = useDebounce(query, 300);

  const handleSearch = useCallback(async (searchQuery: string) => {
    if (searchQuery.trim().length < 2) {
      setResults([]);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const searchResults = await elasticsearchService.searchPlayers(searchQuery);
      setResults(searchResults.hits.hits);
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred during search.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      setQuery(initialQuery || "");
      setResults([]);
      setError(null);
      // Focus the input when the modal opens
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen, initialQuery]);

  // Escape closes the palette.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  // The server list is a 5-minute shared cache, so searching servers costs no request of its own.
  useEffect(() => {
    if (!isOpen) return;
    serverListApiService
      .getServerList()
      .then(setServers)
      .catch(() => setServers([]));
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      handleSearch(debouncedQuery);
      // Also search kanban cards
      if (debouncedQuery.trim().length >= 2) {
        kanbanApiService.searchCards({ q: debouncedQuery.trim() })
          .then(res => setKanbanResults(res.results || []))
          .catch(() => setKanbanResults([]));
      } else {
        setKanbanResults([]);
      }
    }
  }, [debouncedQuery, isOpen, handleSearch]);

  const serverFuse = useMemo(
    () =>
      new Fuse(servers, {
        keys: ["modpackTitle", "serverAddress", "clusterName", "regionName"],
        threshold: 0.35,
        ignoreLocation: true,
      }),
    [servers],
  );

  const serverResults = useMemo(() => {
    const q = debouncedQuery.trim();
    if (q.length < 2) return [];
    return serverFuse.search(q).slice(0, 5).map((hit) => hit.item);
  }, [debouncedQuery, serverFuse]);

  const handleUsernamesUpdated = useCallback(() => {
    results.forEach(result => {
      if (!result._source.lastKnownName) {
        const newName = usernameCacheService.getName(result._source.uuid);
        if (newName) {
          elasticsearchService.updatePlayerName(result._id, newName)
            .catch(err => console.error(`Failed to update name for ${result._source.uuid}:`, err));
        }
      }
    });
  }, [results]);

  useEffect(() => {
    if (results.length > 0) {
      const uuids = results.map((hit: any) => hit._source.uuid);
      usernameCacheService.prefetchUsernames(uuids);
    }

    document.addEventListener('usernamesUpdated', handleUsernamesUpdated);
    return () => {
      document.removeEventListener('usernamesUpdated', handleUsernamesUpdated);
    };
  }, [results, handleUsernamesUpdated]);

  const isPreciseIpSearch = (query: string) => {
    // Match 3 octets and at least one digit of the 4th
    const ipRegex = /^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d+/;
    return ipRegex.test(query);
  };

  const isUUID = (str: string) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(str.trim());

  const queryIsUUID = useMemo(() => isUUID(query), [query]);

  const handleManualSearch = () => {
    if (queryIsUUID) {
      onResultClick(query.trim());
      return;
    }
    handleSearch(query);
  };

  if (!isOpen) {
    return null;
  }

  const showHint = !loading && !error && results.length === 0 && debouncedQuery.length <= 1;
  const showNoResults = !loading && !error && results.length === 0 && debouncedQuery.length > 1 && !queryIsUUID;

  return (
    <div className="modal-overlay z-50 !items-start pt-[10vh] md:pt-[14vh]" role="dialog" aria-modal="true" aria-label="Search" onKeyDown={(event) => {
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
      const actions = Array.from(modalRef.current?.querySelectorAll<HTMLElement>('[data-search-action]') || []);
      if (!actions.length) return;
      const current = actions.indexOf(document.activeElement as HTMLElement);
      if (current < 0 && document.activeElement !== inputRef.current) return;
      event.preventDefault();
      const next = current < 0 ? (event.key === 'ArrowDown' ? 0 : actions.length - 1) : (current + (event.key === 'ArrowDown' ? 1 : -1) + actions.length) % actions.length;
      actions[next].focus();
    }}>
      <div ref={modalRef} className="modal-panel max-w-2xl !p-0 max-h-[75vh]">
        {/* Search field */}
        <div className="flex items-center gap-3 px-4 h-14 border-b border-glass-200/10 flex-shrink-0">
          <Search className="w-4 h-4 text-glass-400 flex-shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
                e.preventDefault();
                if (!query.trim()) return;
                if (queryIsUUID) { handleManualSearch(); return; }
                const firstAction = modalRef.current?.querySelector<HTMLElement>('[data-search-action]');
                if (firstAction) firstAction.click();
                else handleManualSearch();
              }
            }}
            className="flex-1 min-w-0 bg-transparent border-0 outline-none text-base text-white placeholder:text-glass-400 !text-[15px]"
            placeholder="Search players by name, UUID or IP..."
            aria-label="Search Argus"
          />
          {loading && <LoaderCircle className="w-4 h-4 text-glass-400 animate-spin flex-shrink-0" />}
          <button onClick={onClose} className="hidden md:inline-flex kbd" title="Close" aria-label="Close search">Esc</button>
          <button onClick={onClose} className="md:hidden btn-icon btn-icon-sm" title="Close" aria-label="Close search">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="modal-body !mx-0 !px-0 py-2">
          {queryIsUUID && (
            <div className="px-2">
              <button
                data-search-action
                onClick={() => onResultClick(query.trim())}
                className="w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg bg-accent-500/10 border border-accent-400/25 hover:bg-accent-500/15 text-left"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span className="icon-tile icon-tile-sm icon-tile-accent"><ArrowRight className="w-3.5 h-3.5" /></span>
                  <div className="min-w-0">
                    <p className="text-sm text-white font-medium">Go to player profile</p>
                    <p className="text-xs text-glass-400 font-mono truncate">{query.trim()}</p>
                  </div>
                </div>
                <span className="text-xs text-accent-300 inline-flex items-center gap-1.5 flex-shrink-0">
                  <span className="kbd"><CornerDownLeft className="w-3 h-3" /></span>
                </span>
              </button>
            </div>
          )}

          {error && <p className="text-red-400 text-sm text-center px-4 py-6">{error}</p>}

          {showNoResults && (
            <div className="px-4 py-10 text-center">
              <span className="icon-tile icon-tile-lg mb-3"><Users className="w-5 h-5" /></span>
              <p className="text-sm font-medium text-white">No players found</p>
              <p className="text-xs text-glass-400 mt-1">Try a different name, UUID or IP address.</p>
            </div>
          )}

          {showHint && (
            <div className="px-4 py-8 text-center">
              <span className="icon-tile icon-tile-lg icon-tile-accent mb-3"><Search className="w-5 h-5" /></span>
              <p className="text-sm font-medium text-white">Enter at least 2 characters to begin searching.</p>
              <p className="text-xs text-glass-400 mt-1">You can search by username, nickname, UUID, or IP address.</p>
              <p className="text-xs text-glass-500 mt-1">Wildcard (*) and regex searches are supported.</p>
            </div>
          )}

          {results.length > 0 && (
            <div className="px-2">
              <div className="eyebrow px-2 pt-2 pb-1.5 flex items-center gap-2">
                <Users className="w-3.5 h-3.5" />
                Players
                <span className="badge badge-neutral">{results.length}</span>
              </div>
              <div className="space-y-0.5">
                {results.map((result) => (
                  <button
                    data-search-action
                    key={result._id}
                    className="w-full text-left flex items-center justify-between gap-3 px-3 py-2 rounded-lg hover:bg-glass-200/10 focus-visible:bg-glass-200/10"
                    onClick={() => onResultClick(result._source.uuid)}
                  >
                    <PlayerProfile
                      uuid={result._source.uuid}
                      name={result._source.lastKnownName}
                      nickname={result._source.nickname}
                      ipAddress={isPreciseIpSearch(debouncedQuery) && result._source.lastKnownIp.startsWith(debouncedQuery) ? result._source.lastKnownIp : undefined}
                      showUuid={true}
                    />
                    <ArrowRight className="w-4 h-4 text-glass-500 flex-shrink-0" />
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Server Search Results — opens the server's StarChart history */}
          {serverResults.length > 0 && (
            <div className="px-2 mt-2 pt-2 border-t border-glass-200/10">
              <div className="eyebrow px-2 pt-1 pb-1.5 flex items-center gap-2">
                <LineChart className="w-3.5 h-3.5" />
                Servers
                <span className="badge badge-neutral">{serverResults.length}</span>
              </div>
              <div className="space-y-0.5">
                {serverResults.map((server) => (
                  <Link
                    data-search-action
                    key={server.serverAddress}
                    to={`/starchart/${server.serverAddress}`}
                    onClick={onClose}
                    className="w-full text-left flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-glass-200/10"
                  >
                    {server.thumbnailUrl ? (
                      <img
                        src={server.thumbnailUrl}
                        alt=""
                        loading="lazy"
                        className="w-7 h-7 rounded-md object-cover flex-shrink-0"
                      />
                    ) : (
                      <span className="icon-tile icon-tile-sm icon-tile-neutral flex-shrink-0">
                        <LineChart className="w-4 h-4" />
                      </span>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white font-medium truncate">{server.modpackTitle}</p>
                      <p className="text-xs text-glass-400 font-mono truncate">{server.serverAddress}</p>
                    </div>
                    {server.regionName && (
                      <span className="badge badge-neutral flex-shrink-0">{server.regionName}</span>
                    )}
                    <ArrowRight className="w-4 h-4 text-glass-500 flex-shrink-0" />
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Kanban Card Search Results */}
          {kanbanResults.length > 0 && (
            <div className="px-2 mt-2 pt-2 border-t border-glass-200/10">
              <div className="eyebrow px-2 pt-1 pb-1.5 flex items-center gap-2">
                <KanbanSquare className="w-3.5 h-3.5" />
                Kanban Cards
                <span className="badge badge-neutral">{kanbanResults.length}</span>
              </div>
              <div className="space-y-0.5">
                {kanbanResults.slice(0, 5).map((card) => (
                  <Link
                    data-search-action
                    key={card.id}
                    to={`/workspaces/${card.board_id}`}
                    onClick={onClose}
                    className="w-full text-left flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-glass-200/10"
                  >
                    <KanbanCardTypeBadge type={card.card_type as KanbanCardType} showLabel={false} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white font-medium truncate">{card.title}</p>
                      {card.description && (
                        <p className="text-xs text-glass-400 truncate">{card.description}</p>
                      )}
                    </div>
                    <ArrowRight className="w-4 h-4 text-glass-500 flex-shrink-0" />
                  </Link>
                ))}
                {kanbanResults.length > 5 && (
                  <Link
                    data-search-action
                    to="/workspaces"
                    onClick={onClose}
                    className="link text-xs block text-center py-2"
                  >
                    View all {kanbanResults.length} results in Workspaces
                  </Link>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="hidden md:flex items-center justify-between px-4 py-2 border-t border-glass-200/10 text-[11px] text-glass-500 flex-shrink-0">
          <span className="inline-flex items-center gap-1.5"><span className="kbd"><CornerDownLeft className="w-3 h-3" /></span> open</span>
          <span className="inline-flex items-center gap-1.5"><span className="kbd">Esc</span> close</span>
        </div>
      </div>
    </div>
  );
}
